import { X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

type ToastVariant = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  title: string;
  description?: string;
  variant: ToastVariant;
}
type ToastFn = (t: { title: string; description?: string; variant?: ToastVariant }) => void;

const ToastContext = createContext<ToastFn>(() => {});

const accent: Record<ToastVariant, string> = {
  success: 'before:bg-valley',
  error: 'before:bg-danger-700',
  info: 'before:bg-brand-light',
};

const DURATION_MS = 5000;
let nextId = 1;

/**
 * Lightweight toasts in a live region. Deliberately not a dismissable layer, so Escape
 * always reaches the open dialog rather than closing a toast.
 */
function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(() => onDismiss(item.id), DURATION_MS);
    return () => clearTimeout(timer);
  }, [paused, item.id, onDismiss]);

  return (
    <li
      role={item.variant === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={cn(
        'relative flex items-start gap-3 overflow-hidden rounded-card border border-stone bg-white p-4 pl-5 shadow-overlay',
        "before:absolute before:inset-y-0 before:left-0 before:w-1 before:content-['']",
        accent[item.variant],
      )}
    >
      <div className="flex-1">
        <p className="text-sm font-semibold text-ink">{item.title}</p>
        {item.description && <p className="mt-1 text-sm text-slate">{item.description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        aria-label="Dismiss notification"
        className="rounded-full p-1 text-slate hover:bg-mist"
      >
        <X className="size-4" aria-hidden />
      </button>
    </li>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const toast = useCallback<ToastFn>(({ title, description, variant = 'info' }) => {
    setItems((list) => [...list.slice(-2), { id: nextId++, title, description, variant }]);
  }, []);
  const dismiss = useCallback((id: number) => setItems((l) => l.filter((x) => x.id !== id)), []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ol
        aria-label="Notifications"
        className="pointer-events-none fixed inset-x-4 top-4 z-[60] flex flex-col gap-2 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:top-auto sm:w-96 [&>li]:pointer-events-auto"
      >
        {items.map((item) => (
          <Toast key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </ol>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastFn {
  return useContext(ToastContext);
}
